/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import getConfig from '../../config';

declare let adobeIMS: AdobeIMS;

type DocumentAccessServiceRequestBody = {
	docSpec: {
		mimetype: string;
	};
	sourceDocSpec: any;
	directorySpec: any;
	timeZone: string;
	locale: string;
};

class DocumentAccessService {
	static instance: DocumentAccessService;

	/**
	 * Create new untitled Space / repository and retrieve link to edit a new document in new space
	 * @param {directorySpecParentAssetId} directorySpecParentAssetId asset id of the teams folder of which to target directory for creation
	 * @param {directorySpecRepoName} directorySpecRepoName name of default space
	 * @returns object - empty body edit link in response headers
	 */
	async createNewSpace(directorySpecParentAssetId: string, directorySpecRepoName: string) {
		const requestBody = this._constructDocumentServiceRequestBody({
			'repo:name': directorySpecRepoName,
			parent: {
				'repo:assetId': directorySpecParentAssetId,
			},
		}, null);
		return this._performDocumentServiceRequest(requestBody);
	}

	/**
	 * Create new untitled Document into Space/ repository and retrieve link to edit document
	 * @param {directorySpecRepoAssetId} directorySpecRepoAssetId asset id of the teams folder of which to target directory for creation
	 * @param {directorySpecRepositoryId} directorySpecRepositoryId directory id/space target where the document is to be placed
	 * @returns object - empty body edit link in response headers
	 */
	async createNewDocumentInSpace(directorySpecRepoAssetId: string, directorySpecRepositoryId: string) {
		const requestBody = this._constructDocumentServiceRequestBody({
			'repo:assetId': directorySpecRepoAssetId,
			'repo:repositoryId': directorySpecRepositoryId,
		}, null);
		return this._performDocumentServiceRequest(requestBody);
	}

	/**
	 * Create new titled Document copied from a source Document into target Space / Repository
	 * @param {sourceDocSpecAssetId} sourceDocSpecAssetId asset id of the document to be copied
	 * @param {sourceDocSpecRepoName} sourceDocSpecRepoName name of the document created after copy
	 * @param {directorySpecAssetId} directorySpecAssetId asset id of the repository the copy will be placed in
	 * @param {directorySpecRepositoryId} directorySpecRepositoryId repo id of the repository the copy will be placed in
	 * @returns object - empty body edit link in response headers
	 */
	async copyDocumentIntoSpace(sourceDocSpecAssetId: string, sourceDocSpecRepoName: string, directorySpecRepositoryId: string, directorySpecAssetId: string) {
		const requestBody = this._constructDocumentServiceRequestBody({
			'repo:assetId': directorySpecAssetId,
			'repo:repositoryId': directorySpecRepositoryId,
		}, {
			'repo:assetId': sourceDocSpecAssetId,
			'repo:name': sourceDocSpecRepoName,
		});
		return this._performDocumentServiceRequest(requestBody);
	}

	/**
	 * Create a new Space / Repository and then create a new titled Document copied from a source Document into this new Space / Repository
	 * @param {sourceDocSpecAssetId} sourceDocSpecAssetId asset id of the document to be copied
	 * @param {sourceDocSpecRepoName} sourceDocSpecRepoName name of the document created after copy
	 * @param {directorySpecParentAssetId} directorySpecParentAssetId asset id of the teams folder of which to target directory for creation
	 * @param {directorySpecRepoName} directorySpecRepoName name of default space
	 * @returns object - empty body edit link in response headers
	 */
	async copyDocumentIntoNewSpace(sourceDocSpecAssetId: string, sourceDocSpecRepoName: string, directorySpecParentAssetId: string, directorySpecRepoName: string) {
		const requestBody = this._constructDocumentServiceRequestBody({
			'repo:name': directorySpecRepoName,
			parent: {
				'repo:assetId': directorySpecParentAssetId,
			},
		}, {
			'repo:assetId': sourceDocSpecAssetId,
			'repo:name': sourceDocSpecRepoName,
		});
		return this._performDocumentServiceRequest(requestBody);
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	constructor() {
		if (!DocumentAccessService.instance) {
			DocumentAccessService.instance = this;
		}
		return DocumentAccessService.instance;
	}

	_constructDocumentServiceRequestBody(directorySpec?: any, sourceDocSpec?: any) : DocumentAccessServiceRequestBody {
		return {
			docSpec: {
				mimetype: 'application/vnd.adobe.hz.canvas+dcx',
			},
			sourceDocSpec,
			directorySpec,
			// eslint-disable-next-line new-cap
			timeZone: `${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
			locale: window.cchome.util.hasFeature('das_locale') ? `${window.cchome.util.getLocale().replace('_', '-')}` : 'en-US',
		};
	}

	/**
	 * Perform post request to document service with parameters
	 * @param {requestBody} requestBody request body of the post to DAS, includes sourceDocSpec or directorySpec if needed
	 * @returns {Object}
	 */
	_performDocumentServiceRequest(requestBody: DocumentAccessServiceRequestBody): Promise<any> {
		return new Promise((resolve, reject) => {
			const request = https.request(
				{
					hostname: window.cchome.util.hasFeature('das_ethos')
						? getConfig('SERVICES').document.ethosHostname
						: getConfig('SERVICES').document.hostname,
					path: getConfig('SERVICES').document.getDocumentPath,
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: '*/*',
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					let data = '';
					res.setEncoding('utf8');
					res.on('data', (chunk) => {
						data += chunk;
					});

					if (res.statusCode && res.statusCode >= 300) {
						res.on('end', () => {
							try {
								window.cchome.util.logger.error(
									'Document Service',
									`Problem with Document Access Service response: status code ${res.statusCode}`,
									JSON.parse(data),
								);
								const DocumentServiceError = new Error('_performDocumentServiceRequest error');
								reject(DocumentServiceError);
							}
							catch (e) {
								reject(e);
							}
						});
					}
					else {
						res.on('end', () => {
						// get location header which gives us the url we need
							try {
								resolve(res.headers.location);
							}
							catch (e) {
								reject(e);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
			// on error, display the search error message
				window.cchome.util.logger.error(
					'Document Service',
					'Problem with Document Access Service request',
					err.toString(),
				);
				const DocumentServiceError = new Error('_performDocumentServiceRequest error');
				reject(DocumentServiceError);
			});

			request.write(
				JSON.stringify(requestBody),
			);
			request.end();
		});
	}
}

export default DocumentAccessService;
